import {
  buildEmailHeaderRow,
  EMAIL_HEADER_CELL_STYLE,
} from "./emailProductLogo";
import { buildEmailNeedHelpSection } from "./emailSupportFooter";

export type WorkflowEmailStage =
  | "customer_submitted"
  | "implementation_submitted"
  | "workflow_complete";

export type WorkflowEmailData = {
  stage: WorkflowEmailStage;
  submitterFirstName: string;
  submitterLastName: string;
  customerName: string;
  productId: number;
  productName: string;
  onboardingUrl: string;
};

type StageCopy = {
  badge: string;
  title: string;
  intro: string;
  detail: string;
  statusLabel: string;
  statusValue: string;
  cta: string;
};

const stageCopy = (data: WorkflowEmailData): StageCopy => {
  const submitter = [data.submitterFirstName, data.submitterLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const submitterLabel = submitter || "A team member";

  switch (data.stage) {
    case "customer_submitted":
      return {
        badge: "Customer submitted",
        title: "Settings template submitted for review",
        intro: `${submitterLabel} submitted the onboarding settings template.`,
        detail:
          "The implementation team can now review the template, provide feedback, and prepare it for TPM final approval.",
        statusLabel: "Current stage",
        statusValue: "Awaiting implementation team review",
        cta: "Review settings template",
      };
    case "implementation_submitted":
      return {
        badge: "Implementation review complete",
        title: "Settings template ready for TPM review",
        intro: `${submitterLabel} completed the implementation team review.`,
        detail:
          "The technical project manager can now perform the final review and complete onboarding.",
        statusLabel: "Current stage",
        statusValue: "Awaiting TPM final review",
        cta: "Open onboarding portal",
      };
    case "workflow_complete":
      return {
        badge: "Onboarding complete",
        title: "Settings template fully approved",
        intro: `${submitterLabel} completed the final TPM review.`,
        detail:
          "All onboarding review stages are complete. The settings template has been finalized for this customer.",
        statusLabel: "Current stage",
        statusValue: "Onboarding complete",
        cta: "View onboarding portal",
      };
    default:
      return {
        badge: "Workflow update",
        title: "Onboarding workflow updated",
        intro: `${submitterLabel} updated the onboarding workflow.`,
        detail: "Please sign in to review the latest status.",
        statusLabel: "Status",
        statusValue: "Updated",
        cta: "Open onboarding portal",
      };
  }
};

export const workflowEmailSubject = (data: WorkflowEmailData): string => {
  const customer = data.customerName.toUpperCase();
  const product = data.productName.toUpperCase();
  switch (data.stage) {
    case "customer_submitted":
      return `Settings template submitted for review — ${customer} ${product}`;
    case "implementation_submitted":
      return `Implementation review complete — ${customer} ${product}`;
    case "workflow_complete":
      return `Onboarding complete — ${customer} ${product}`;
    default:
      return `Onboarding workflow update — ${customer} ${product}`;
  }
};

export const buildWorkflowEmailHtml = (data: WorkflowEmailData): string => {
  const copy = stageCopy(data);
  const emailHeader = buildEmailHeaderRow(data.productId, data.productName);
  const greeting = "Hello,";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${workflowEmailSubject(data)}</title>
</head>
<body style="margin:0;padding:0;background-color:#eef2f7;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:linear-gradient(180deg,#ecfdf5 0%,#eef2f7 28%,#eef2f7 100%);padding:40px 16px 48px;">
    <tr>
      <td align="center">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:600px;">
          <tr>
            <td style="padding:0 0 14px;text-align:center;">
              <span style="display:inline-block;padding:6px 14px;border-radius:999px;background:#ffffff;border:1px solid #d1fae5;color:#047857;font-size:12px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;box-shadow:0 2px 8px rgba(16,185,129,0.12);">
                ${copy.badge}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#ffffff;border-radius:18px;border:1px solid #e5e7eb;overflow:hidden;box-shadow:0 18px 45px rgba(15,23,42,0.08);">
                <tr>
                  <td style="height:6px;background:linear-gradient(90deg,#10b981 0%,#059669 50%,#34d399 100%);font-size:0;line-height:0;">&nbsp;</td>
                </tr>
                <tr>
                  <td style="${EMAIL_HEADER_CELL_STYLE}">
                    ${emailHeader}
                  </td>
                </tr>
                <tr>
                  <td style="padding:34px 36px 10px;">
                    <h1 style="margin:0 0 10px;font-size:28px;line-height:1.2;font-weight:800;color:#0f172a;letter-spacing:-0.03em;">
                      ${copy.title}
                    </h1>
                    <p style="margin:0 0 14px;font-size:16px;line-height:1.7;color:#475569;">${greeting}</p>
                    <p style="margin:0 0 14px;font-size:16px;line-height:1.7;color:#475569;">${copy.intro}</p>
                    <p style="margin:0;font-size:16px;line-height:1.7;color:#475569;">${copy.detail}</p>
                  </td>
                </tr>
                <tr>
                  <td style="padding:18px 36px 8px;">
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-radius:14px;background:linear-gradient(135deg,#f0fdf4 0%,#ecfeff 100%);border:1px solid #bbf7d0;">
                      <tr>
                        <td style="padding:16px 18px;">
                          <p style="margin:0 0 4px;font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#059669;">Customer</p>
                          <p style="margin:0 0 12px;font-size:18px;font-weight:700;color:#0f172a;">${data.customerName}</p>
                          <p style="margin:0 0 4px;font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#059669;">${copy.statusLabel}</p>
                          <p style="margin:0;font-size:15px;font-weight:600;color:#0f172a;">${copy.statusValue}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="padding:22px 36px 34px;" align="center">
                    <a href="${data.onboardingUrl}" style="display:inline-block;min-width:240px;padding:15px 32px;background:linear-gradient(145deg,#10b981 0%,#059669 100%);color:#ffffff;text-decoration:none;font-size:16px;font-weight:700;border-radius:12px;box-shadow:0 10px 24px rgba(16,185,129,0.35);text-align:center;">
                      ${copy.cta} &rarr;
                    </a>
                    <p style="margin:18px 0 0;font-size:13px;line-height:1.6;color:#94a3b8;">
                      Or open this link in your browser:<br />
                      <a href="${data.onboardingUrl}" style="color:#059669;text-decoration:none;word-break:break-all;">${data.onboardingUrl}</a>
                    </p>
                  </td>
                </tr>
                ${buildEmailNeedHelpSection()}
                <tr>
                  <td style="padding:22px 36px 28px;background:#f8fafc;border-top:1px solid #e2e8f0;text-align:center;">
                    <p style="margin:0 0 6px;font-size:13px;line-height:1.6;color:#64748b;font-weight:600;">Agilysys Onboarding</p>
                    <p style="margin:0;font-size:12px;line-height:1.6;color:#94a3b8;">
                      This notification was sent because a workflow step was completed.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};
